import { defineComponent as _defineComponent } from 'vue'
import { toDataModel, updateModel, getChildModel, setState, resetForm } from 'ah-common-lib/src/form/helpers';
import { KycCompanyDetails, Address, CompanyRegistrationModel } from 'ah-api-gateways';
import { ValidatedForm } from 'ah-common-lib/src/form/components';
import { FormValidation, FormEvent, FormDefinition } from 'ah-common-lib/src/form/interfaces';
import { cloneDeep, debounce, DebouncedFunc, isEqual } from 'lodash';
import { FormSelectFieldSearchEvent } from 'ah-common-lib/src/form/interfaces';
import { useSettingsStore } from '@/app/store/settingsModule';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import { getServices } from '@/app/services';
import RegistrationAddressForm from '../common/RegistrationAddressForm.vue';
import { makeCompanyDetailsFM, makeDuplicateAddressFM } from '@/app/helpers/registration/forms';
import { generateKYCAddress, isKYCCountry } from '@/app/helpers/registration/kyc';
import { mergeMap } from 'rxjs/operators';
import { waitForEntityChange } from 'ah-requests';
import { from } from 'rxjs';
import { handleApiRegistrationError } from '@/app/helpers/registration/requests';
import { useAuthStore } from '@/app/store/authModule';


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyDetailsForm',
  props: {
    model: null
  },
  emits: ["proceed", "update:model", "update:validation"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'proceed', value: void): void;
  (e: 'update:model', value: Partial<CompanyRegistrationModel>): void;
  (e: 'update:validation', value: FormValidation<CompanyRegistrationModel>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { model: Partial<CompanyRegistrationModel> };

const services = getServices();
const requestManager = useRequestManager().manager;
const settingsStore = useSettingsStore();
const authStore = useAuthStore();

const kycSearchMinLength = 3;





const companyRegistrationForm = ref<InstanceType<typeof RegistrationAddressForm> | null>(null);
const tradingRegistrationForm = ref<InstanceType<typeof RegistrationAddressForm> | null>(null);

const state = reactive<{
  companyAddress: Partial<Address>;
  tradingAddress: Partial<Address>;
  countryValidation: FormValidation | null;
  companyAddressValidation: FormValidation | null;
  tradingAddressValidation: FormValidation | null;
  debouncedSearch?: DebouncedFunc<(search: string) => void>;
  search: string;
  kycCompany: KycCompanyDetails | null;
}>({
  companyAddress: {},
  tradingAddress: {},
  countryValidation: null,
  companyAddressValidation: null,
  tradingAddressValidation: null,
  search: '',
  kycCompany: null,
});

const companyDetailsFM = reactive<FormDefinition>({
  form: makeCompanyDetailsFM(),
  validation: null,
});

const duplicatedAddressFM = reactive<FormDefinition>({
  form: makeDuplicateAddressFM(),
  validation: null,
});

const kycCountries = computed(() => settingsStore.KYCCountries);

const isDuplicatedAddress = computed(() => duplicatedAddressFM.form.duplicatedAddress);

const calculatedModel = computed(() => {
  const address = { ...props.model.address, ...state.companyAddress } as Address;
  let tradingAddress: Address;
  if (isDuplicatedAddress.value) {
    tradingAddress = cloneDeep(address);
    tradingAddress.countryCode = props.model.address?.countryCode!;
  } else {
    tradingAddress = {
      ...props.model.tradingAddress,
      ...state.tradingAddress,
    } as Address;
  }

  const companyDetails = { ...toDataModel(companyDetailsFM.form), tradingAddress, address };

  const kycCompany = isKYCCountry(kycCountries.value, props.model.address?.countryCode) ? state.kycCompany : null;

  delete companyDetails.kycCompanyName;
  delete companyDetails.userTypes;
  delete companyDetails.noWebsite;

  Object.keys(companyDetails).forEach((k) => {
    if (companyDetails[k] === '') {
      delete companyDetails[k];
    }
  });

  return {
    ...props.model,
    address,
    companyDetails,
    kycCompany,
    duplicatedAddress: isDuplicatedAddress.value,
  } as Partial<CompanyRegistrationModel>;
});

const validation = computed(() => {
  return {
    $model: calculatedModel.value,
    $invalid: !!(
      companyDetailsFM.validation?.$invalid ||
      state.countryValidation?.$invalid ||
      state.companyAddressValidation?.$invalid ||
      (!isDuplicatedAddress.value && state.tradingAddressValidation?.$invalid)
    ),
    $dirty: !!(
      companyDetailsFM.validation?.$dirty ||
      state.countryValidation?.$dirty ||
      state.companyAddressValidation?.$anyDirty ||
      state.tradingAddressValidation?.$anyDirty
    ),
  } as FormValidation<any>;
});

const countryCode = computed(() => props.model.address?.countryCode);

onBeforeMount(() => {
  state.debouncedSearch = debounce((search: string) => (state.search = search), 500);
  state.kycCompany = props.model.kycCompany || null;

  // Set up country if current selects is part of KYC
  settingsStore.loadKYCCountries(true).then(() => {
    if (
      !!props.model.companyDetails?.name &&
      !!props.model.address?.countryCode &&
      isKYCCountry(kycCountries.value, props.model.address.countryCode)
    ) {
      state.search = props.model.companyDetails.name;
      setTimeout(() => (companyDetailsFM.form.kycCompanyName = state.search));
    }
  });

  updateModelFromKycCompany();

  // Set up entity type options
  const entityType = getChildModel(companyDetailsFM.form, 'entityType')!;
  setState(entityType, 'options', []);

  settingsStore.loadEntityTypes(true).then((types) => {
    const typesMapped = types.map((t) => ({ label: t.legalForm, value: t.id }));
    setState(entityType, 'options', typesMapped);
  });
});

watch(
  () => companyDetailsFM.form.entityType,
  () => {
    const kycCompanyName = getChildModel(companyDetailsFM.form, 'kycCompanyName')!;
    const companyName = getChildModel(companyDetailsFM.form, 'name')!;
    const registrationNumber = getChildModel(companyDetailsFM.form, 'registrationNumber')!;

    if (companyDetailsFM.form.entityType === 'CHARITABLE_TRUST') {
      setState(kycCompanyName, 'title', 'Charity name');
      setState(companyName, 'title', 'Charity name');
      setState(registrationNumber, 'title', 'Charity number');
      setState(registrationNumber, 'required', true);
    } else if (companyDetailsFM.form.entityType === 'SOLE_TRADER') {
      setState(kycCompanyName, 'title', 'Trading name');
      setState(companyName, 'title', 'Trading name');
      setState(registrationNumber, 'title', 'VAT number');
      setState(registrationNumber, 'required', false);
    } else {
      setState(kycCompanyName, 'title', 'Registered Company name');
      setState(companyName, 'title', 'Registered Company name');
      setState(registrationNumber, 'title', 'Company number');
      setState(registrationNumber, 'required', true);
    }
  },
  { immediate: true }
);

function checkWebsite() {
  if (companyDetailsFM.form.noWebsite) {
    companyDetailsFM.form.website = null;
    resetForm(companyDetailsFM.validation!);
  }
  setState(getChildModel(companyDetailsFM.form, 'website')!, 'required', !companyDetailsFM.form.noWebsite);
  setState(getChildModel(companyDetailsFM.form, 'website')!, 'readonly', companyDetailsFM.form.noWebsite);
}

function checkKYCCountries() {
  const companyName = getChildModel(companyDetailsFM.form, 'name')!;
  const kycCompanyName = getChildModel(companyDetailsFM.form, 'kycCompanyName')!;
  const isKyc = isKYCCountry(kycCountries.value, countryCode.value);
  setState(companyName, 'hidden', isKyc);
  setState(kycCompanyName, 'hidden', !isKyc);
  setState(kycCompanyName, 'required', isKyc);
  return !!isKyc;
}

function onFormEvent(event: FormEvent) {
  const isKYCCompanyField = event.model.$name === 'kycCompanyName';

  if (event.event === 'form-field-set-value') {
    checkWebsite();
    setState(companyDetailsFM.form, 'errors', [], true);

    if (isKYCCompanyField) {
      const company = companyDetailsFM.form.kycCompanyName as KycCompanyDetails;

      if (company.rawName && company.externalCode) {
        state.kycCompany = company;
        companyDetailsFM.form.name = company.rawName;
        companyDetailsFM.form.registrationNumber = company.externalCode;
        state.companyAddress = {
          addressLine: company.rawAddress,
          city: company.city,
          postalCode: company.zip,
          stateOrProvince: company.city,
        };

        companyDetailsFM.form.kycCompanyName = company.rawName;
      } else {
        state.kycCompany = null;
        // allow set of a not found kyc company
        updateModel(
          companyDetailsFM.form,
          { name: company.rawName, kycCompanyName: company.rawName, entityType: companyDetailsFM.form.entityType },
          ['name', 'kycCompanyName', 'entityType'],
          true
        );
        state.companyAddress = {};
      }
      updateModelFromKycCompany();
    }
    emit('update:model', calculatedModel.value);
  } else if (isKYCCompanyField && event.event === 'form-field-select-search') {
    state.debouncedSearch!((event as FormSelectFieldSearchEvent).search);
  }
}

function updateModelFromKycCompany() {
  const kycContainsInfo = (name: 'addressLine' | 'postalCode' | 'city' | 'registrationNumber' | 'stateOrProvince') => {
    switch (name) {
      case 'addressLine':
        return !!state.kycCompany?.rawAddress;
      case 'postalCode':
        return !!state.kycCompany?.zip;
      case 'city':
        return !!state.kycCompany?.city;
      case 'registrationNumber':
        return !!state.kycCompany?.externalCode;
      default:
        return false;
    }
  };

  const addressLine = companyRegistrationForm.value?.getModel('addressLine');
  const addressPostCode = companyRegistrationForm.value?.getModel('postalCode');
  const addressStateOrProvince = companyRegistrationForm.value?.getModel('stateOrProvince');
  const addressCity = companyRegistrationForm.value?.getModel('city');
  const registrationNumber = getChildModel(companyDetailsFM.form, 'registrationNumber');

  const fields = [addressLine, addressPostCode, addressStateOrProvince, addressCity, registrationNumber];

  const isKyc = isKYCCountry(kycCountries.value, props.model.address?.countryCode);

  fields.forEach((model) => {
    if (model) {
      const readonly = isKyc && kycContainsInfo(model.$name as any);
      setState(model, 'inputGroupClass', readonly ? 'background-transparent' : '');
      setState(model, 'readonly', readonly);
      setState(model, 'required', !readonly && companyDetailsFM.form.entityType !== 'SOLE_TRADER');
    }
  });
}

function clearForms() {
  state.search = '';
  state.kycCompany = null;
  updateModel(companyDetailsFM.form, {}, undefined, true);
  state.companyAddress = {};
  state.tradingAddress = {};

  if (companyDetailsFM.validation) {
    resetForm(companyDetailsFM.validation);
  }
  if (state.companyAddressValidation) {
    resetForm(state.companyAddressValidation);
  }
  if (state.tradingAddressValidation) {
    resetForm(state.tradingAddressValidation);
  }
}

function noEntries() {
  if (state.search.length > 0 && state.search.trim().length > 0) {
    setState(getChildModel(companyDetailsFM.form, 'kycCompanyName')!, 'options', [
      { value: { rawName: state.search }, label: state.search },
      { value: 'noEntries', label: 'No entries found' },
    ]);
  } else {
    setState(getChildModel(companyDetailsFM.form, 'kycCompanyName')!, 'options', [
      { value: 'noEntries', label: 'No entries found' },
    ]);
  }
}

function submit() {
  requestManager
    .sameOrCancelAndNew(
      'createClient',
      services.registration
        .registerClient({
          companyDetails: calculatedModel.value.companyDetails,
        })
        .pipe(
          // Waiting for session update until a client is added to the session data (due to CQRS delay)
          mergeMap(() =>
            waitForEntityChange(
              () =>
                from(
                  useAuthStore()
                    .refreshSession()
                    .then(() => authStore.userData)
                ),
              (session) => !!session?.individual?.client?.id
            )
          )
        )
    )
    .subscribe(
      () => emit('proceed'),
      (e) => handleApiRegistrationError(e, companyDetailsFM.form, companyDetailsFM.validation!)
    );
}

watch(
  countryCode,
  () => {
    clearForms();
    checkKYCCountries();
    updateModelFromKycCompany();
  },
  { immediate: true }
);

watch(
  () => duplicatedAddressFM.form.duplicatedAddress,
  () => emit('update:model', calculatedModel.value)
);

watch(
  validation,
  (newVal, oldVal) => {
    if (!isEqual(newVal, oldVal)) {
      emit('update:validation', validation.value);
    }
  },
  { immediate: true }
);

watch(
  () => state.search,
  () => {
    requestManager.cancel('getCompanyAddressDetails');
    if (countryCode.value && state.search.length > 0) {
      if (state.search.trim().length >= kycSearchMinLength) {
        setState(getChildModel(companyDetailsFM.form, 'kycCompanyName')!, 'options', [
          { value: { rawName: state.search }, label: state.search },
          { value: 'loadingOptions', label: 'Loading more options' },
        ]);
        requestManager
          .cancelAndNew(
            'getCompanyAddressDetails',
            services.customerReference.kycCountryInformation({
              countryCode: countryCode.value,
              query: state.search,
            })
          )
          .subscribe(
            (companies) => {
              if (companies.length > 0) {
                if (state.search.trim().length > 0) {
                  setState(getChildModel(companyDetailsFM.form, 'kycCompanyName')!, 'options', [
                    { value: { rawName: state.search }, label: state.search },
                    ...companies.map((company) => ({ value: company, label: company.rawName })),
                  ]);
                } else {
                  setState(getChildModel(companyDetailsFM.form, 'kycCompanyName')!, 'options', [
                    ...companies.map((company) => ({ value: company, label: company.rawName })),
                  ]);
                }
              } else noEntries();
            },
            () => noEntries()
          );
      } else {
        setState(getChildModel(companyDetailsFM.form, 'kycCompanyName')!, 'options', [
          { value: { rawName: state.search }, label: state.search },
          {
            value: 'minSearchRequired',
            label: `Please type at least ${kycSearchMinLength} characters to search for a company.`,
          },
        ]);
      }
    }
  }
);

watch(
  () => props.model,
  () => {
    state.companyAddress = cloneDeep(props.model.address ?? {});
    updateModel(companyDetailsFM.form, props.model.companyDetails ?? {});

    if (props.model.duplicatedAddress !== undefined) {
      duplicatedAddressFM.form.duplicatedAddress = props.model.duplicatedAddress;
    }

    if (!isDuplicatedAddress.value) {
      state.tradingAddress = cloneDeep(props.model.companyDetails?.tradingAddress ?? {});
    }
  },
  { immediate: true }
);

return { __sfc: true,services, requestManager, settingsStore, authStore, kycSearchMinLength, props, emit, companyRegistrationForm, tradingRegistrationForm, state, companyDetailsFM, duplicatedAddressFM, kycCountries, isDuplicatedAddress, calculatedModel, validation, countryCode, checkWebsite, checkKYCCountries, onFormEvent, updateModelFromKycCompany, clearForms, noEntries, submit, ValidatedForm, RegistrationAddressForm, generateKYCAddress }
}

})